import { addDays, format, formatDate, isSameDay, startOfToday } from "date-fns";
import { DaysOfWeek, TimeOfDay } from "~/entities/baseEntities";

/**
 * Returns as format e.g. "12/04/23".
 */
export const formatDateTime = (date: Date) => {
  if (isSameDay(startOfToday(), date)) {
    return "Today";
  }
  if (isSameDay(addDays(startOfToday(), 1), date)) {
    return "Tomorrow";
  }
  return format(date, "dd/MM/yy");
};

// format to dd.MM.yyyy
export const formatDateValue = (date: Date) => {
  return format(date, "dd.MM.yyyy");
};

/**
 * Returns as format e.g. "12th Apr".
 */
export const formatDay = (date: Date) => {
  if (isSameDay(startOfToday(), date)) {
    return "Today";
  }
  if (isSameDay(addDays(startOfToday(), 1), date)) {
    return "Tomorrow";
  }
  return formatDate(date, "do MMM");
};

/**
 * Returns as format e.g. "1:23 PM"
 * @param dateTime
 */
export const formatTime = (dateTime: Date): string => {
  return format(dateTime, "h:mm a");
};

/**
 * Returns as format e.g. "1:23 PM tomorrow"
 * @param dateTime
 */
export const formatTimeAndDay = (dateTime: Date): string => {
  const time = formatTime(dateTime);
  if (isSameDay(startOfToday(), dateTime)) {
    return time;
  }
  const day = formatDay(dateTime);
  return `${day} ${time}`;
};

export const timeFromObject = (value: TimeOfDay | null) => {
  if (!value) return null;

  const date = new Date().setHours(value.hour, value.minute, 0);

  return format(date, "h:mm aa");
};

export const combineDays = (applies: DaysOfWeek) => {
  const days = Object.values(applies).reduce(
    (res: number[], cur, index) => (cur ? [...res, index] : res),
    []
  );
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  days.sort((a, b) => a - b);
  const ranges = [];
  let lastDay = null;

  for (const day of days) {
    if (lastDay === null || day - lastDay !== 1) {
      ranges.push([day]);
    } else {
      ranges[ranges.length - 1].push(day);
    }
    lastDay = day;
  }

  let output = "";
  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i];
    if (range.length === 1) {
      output += daysOfWeek[range[0]];
    } else {
      output += `${daysOfWeek[range[0]]}-${
        daysOfWeek[range[range.length - 1]]
      }`;
    }
    if (i !== ranges.length - 1) {
      output += ", ";
    }
  }
  return output;
};

/**
 * Returns a date in the past relative to today.
 * @param yearsAgo Number of years in the past.
 */
export const getDateYearsAgo = (yearsAgo: number): string => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - yearsAgo);
  return date.toISOString().split("T")[0];
};

export const getWaitTime = (from: Date, to: Date): string => {
  const diffMs = Math.max(0, to.getTime() - from.getTime());
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `(~${hours} h ${minutes} min wait)`;
  if (hours > 0) return `(~${hours} h wait)`;
  return `~(${minutes} min wait)`;
};
